import React, { useLayoutEffect, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLenisRef } from '../context/ScrollContext';
import { CORE_PROJECTS } from '../constants';
import content from '../i18n/content';
import './ProjectDetail.css';

function ScreenshotCarousel({ images, title }) {
  // Clone-based infinite loop: [last, ...images, first]
  const cloned = [images[images.length - 1], ...images, images[0]];
  const [index, setIndex] = useState(1);
  const [animated, setAnimated] = useState(true);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);
  const isTransitioning = useRef(false);
  const indexRef = useRef(1);

  const dotIndex = ((index - 1) + images.length) % images.length;

  const goTo = useCallback((i) => {
    if (isTransitioning.current) return;
    if (indexRef.current === i) return;
    isTransitioning.current = true;
    indexRef.current = i;
    setAnimated(true);
    setIndex(i);
  }, []);

  const prev = useCallback(() => goTo(indexRef.current - 1), [goTo]);
  const next = useCallback(() => goTo(indexRef.current + 1), [goTo]);

  const onTransitionEnd = useCallback(() => {
    isTransitioning.current = false;
    if (indexRef.current === 0) {
      const target = images.length;
      indexRef.current = target;
      setAnimated(false);
      setIndex(target);
    } else if (indexRef.current === cloned.length - 1) {
      const target = 1;
      indexRef.current = target;
      setAnimated(false);
      setIndex(target);
    }
  }, [images.length, cloned.length]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <div className="screenshot-carousel-wrapper">
      <div className="screenshot-carousel" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          ref={trackRef}
          className="screenshot-carousel-track"
          style={{
            transform: `translateX(-${index * (100 / cloned.length)}%)`,
            transition: animated ? 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            width: `${cloned.length * 100}%`,
          }}
          onTransitionEnd={onTransitionEnd}
        >
          {cloned.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${title} screenshot ${((i - 1 + images.length) % images.length) + 1}`}
              className="screenshot-carousel-img"
              style={{ width: `${100 / cloned.length}%` }}
              draggable={false}
            />
          ))}
        </div>

        {images.length > 1 && (
          <>
            <button type="button" className="screenshot-carousel-btn screenshot-carousel-btn--prev" onClick={prev} aria-label="Previous screenshot">
              <ChevronLeft size={20} />
            </button>
            <button type="button" className="screenshot-carousel-btn screenshot-carousel-btn--next" onClick={next} aria-label="Next screenshot">
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="screenshot-carousel-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`screenshot-carousel-dot${i === dotIndex ? ' screenshot-carousel-dot--active' : ''}`}
              onClick={() => goTo(i + 1)}
              aria-label={`Go to screenshot ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SpriteRunner({ dir, frameCount, fps, name }) {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setFrame(f => (f + 1) % frameCount);
    }, 1000 / fps);
    return () => clearInterval(id);
  }, [frameCount, fps]);

  const padded = String(frame).padStart(2, '0');
  const src = `${process.env.PUBLIC_URL}/${dir}/frame-${padded}.svg`;

  return (
    <img
      src={src}
      alt={name}
      className="sprite-runner"
      draggable={false}
    />
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const lenisRef = useLenisRef();

  useLayoutEffect(() => {
    const lenis = lenisRef.current;
    if (lenis) {
      lenis.resize();
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [lenisRef]);

  const handleBack = () => navigate('/', { state: { scrollTo: 'projects' } });
  const t = content[lang].projects;

  const projectData = CORE_PROJECTS[id];
  const projectCopy = t.items[id];

  if (!projectData || !projectCopy) {
    return (
      <div className="project-detail-not-found">
        <p>{t.notFound}</p>
        <button type="button" onClick={handleBack} className="project-detail-back">
          <ArrowLeft size={18} />
          <span>{t.backToPortfolio}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      <button type="button" className="project-detail-back" onClick={handleBack}>
        <ArrowLeft size={18} />
        <span>{t.back}</span>
      </button>

      <h1>{projectCopy.title}</h1>
      <p className="project-detail-description">{projectCopy.description}</p>

      <h2>{t.keyAchievements}</h2>
      <ul>
        {projectCopy.achievements.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>

      <p className="project-detail-stack">
        <strong>{t.techStack}</strong> {projectData.stack.join(', ')}
      </p>

      <a href={projectData.url} target="_blank" rel="noopener noreferrer" className="project-detail-github">
        {t.viewGithub}
        <ExternalLink size={16} />
      </a>

      {projectData.screenshots?.length > 0 && (
        <ScreenshotCarousel images={projectData.screenshots} title={projectCopy.title} />
      )}

      {!projectData.screenshots && projectData.screenshot && (
        <img
          src={projectData.screenshot}
          alt={`${projectCopy.title} screenshot`}
          className="project-detail-screenshot"
          loading="lazy"
        />
      )}

      {projectData.runners && (
        <div className="project-detail-runners">
          {projectData.runners.players.map((player) => (
            <SpriteRunner
              key={player.dir}
              dir={player.dir}
              name={player.name}
              frameCount={projectData.runners.frameCount}
              fps={projectData.runners.fps}
            />
          ))}
        </div>
      )}

      {!projectData.runners && projectData.avatars?.length > 0 && (
        <div className="project-detail-avatars">
          {projectData.avatars.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Player ${i + 1}`}
              className="project-detail-avatar-img"
              loading="lazy"
            />
          ))}
        </div>
      )}
    </div>
  );
}
